import React from 'react';
import { SolutionCardProps } from '../../types/components';

const SolutionCard: React.FC<SolutionCardProps> = ({ solution, variant, onClick }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(solution.id);
    } else {
      window.open(`/solutions/${solution.id}`, '_blank', 'noopener,noreferrer');
    }
  };

  // Get color styling based on industry grouping
  const getIndustryColors = (industry: string) => {
    // Group 1: Horizontal
    if (industry === 'Horizontal') {
      return {
        border: 'border-purple-400',
        bg: 'bg-purple-100',
        text: 'text-purple-800'
      };
    }
    
    // Group 2: Financial sector
    if (['Banking', 'Insurance', 'Financial Services'].includes(industry)) {
      return {
        border: 'border-emerald-400',
        bg: 'bg-emerald-100',
        text: 'text-emerald-800'
      };
    }
    
    // Group 3: Manufacturing
    if (['Manufacturing', 'Automotive'].includes(industry)) {
      return {
        border: 'border-amber-400',
        bg: 'bg-amber-100',
        text: 'text-amber-800'
      };
    }
    
    // Group 4: Everything else
    const group4Industries = [
      'Education',
      'Energy and Utilities',
      'Life Sciences and Healthcare',
      'Telecom, Media and Entertainment'
    ];
    
    if (group4Industries.includes(industry)) {
      // Assign different colors to each industry in group 4
      const colorMap: { [key: string]: { border: string; bg: string; text: string } } = {
        'Education': { border: 'border-blue-400', bg: 'bg-blue-100', text: 'text-blue-800' },
        'Energy and Utilities': { border: 'border-yellow-400', bg: 'bg-yellow-100', text: 'text-yellow-800' },
        'Life Sciences and Healthcare': { border: 'border-rose-400', bg: 'bg-rose-100', text: 'text-rose-800' },
        'Telecom, Media and Entertainment': { border: 'border-cyan-400', bg: 'bg-cyan-100', text: 'text-cyan-800' }
      };
      return colorMap[industry];
    }
    
    // Default for any undefined industries
    return {
      border: 'border-gray-300',
      bg: 'bg-gray-100',
      text: 'text-gray-800'
    };
  };

  const industryColors = getIndustryColors(solution.industry);

  if (variant === 'list') {
    return (
      <button
        className={`w-full bg-white rounded-lg shadow-sm border ${industryColors.border} p-4 sm:p-6 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-left touch-manipulation`}
        onClick={handleClick}
        aria-label={`View details for ${solution.title}`}
      >
        <div className="flex items-start space-x-3 sm:space-x-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1 mb-3 sm:mb-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {solution.title}
                </h3>
                <div className="flex flex-wrap gap-1 mb-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${industryColors.bg} ${industryColors.text}`}
                  >
                    {solution.industry}
                  </span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{solution.summary}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-2 sm:space-x-4 text-sm text-gray-500">
                <span>{solution.velocityEstimation}</span>
                <span>•</span>
                <span>{solution.arrEstimation}</span>
              </div>
              <div className="flex items-center text-blue-600">
                <span className="text-sm font-medium">View Details</span>
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </button>
    );
  }

  // Grid variant (default)
  return (
    <button
      className={`w-full h-full bg-white rounded-lg shadow-sm border ${industryColors.border} p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-blue-400 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 group text-left touch-manipulation flex flex-col`}
      onClick={handleClick}
      aria-label={`View details for ${solution.title}`}
    >
      {/* Title */}
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[3.5rem]">
          {solution.title}
        </h3>
        {/* Industry tag below title */}
        <div className="flex flex-wrap gap-1.5">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${industryColors.bg} ${industryColors.text}`}
          >
            {solution.industry}
          </span>
        </div>
      </div>
      
      {/* Summary */}
      <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-3 flex-grow">
        {solution.summary}
      </p>
      
      {/* Metadata - only show if available */}
      {(solution.velocityEstimation || solution.arrEstimation) && (
        <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
          {solution.velocityEstimation && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Timeline:</span>
              <span className="font-medium text-gray-900">{solution.velocityEstimation}</span>
            </div>
          )}
          {solution.arrEstimation && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">ARR Potential:</span>
              <span className="font-medium text-gray-900">{solution.arrEstimation}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Footer with AWS services and CTA */}
      <div className="flex items-center justify-between pt-3 mt-auto">
        <div className="flex items-center space-x-2">
          {solution.awsServices.slice(0, 3).map((service) => (
            <div
              key={service.name}
              className="w-7 h-7 bg-orange-100 rounded text-xs flex items-center justify-center font-medium text-orange-800"
              title={service.name}
            >
              {service.name.split(' ').map(word => word[0]).join('').slice(0, 2)}
            </div>
          ))}
          {solution.awsServices.length > 3 && (
            <span className="text-xs text-gray-500 font-medium">+{solution.awsServices.length - 3}</span>
          )}
        </div>
        
        <div className="flex items-center text-blue-600 group-hover:text-blue-700 transition-colors">
          <span className="text-sm font-medium">Learn More</span>
          <svg className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
};

export default SolutionCard;